import IntranetLayout from "@/components/IntranetLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Shield, AlertTriangle, FileText, CheckCircle2, Users, Calendar, Download, Eye, ExternalLink, Lock, Filter, GraduationCap } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useState, useMemo } from "react";
import { useDocumentReadingTime } from "@/hooks/useDocumentReadingTime";
import VelocityHSECourse from "@/components/VelocityHSECourse";
import { HSESuiteContent } from "./HSESuite";
import { ComplianceWatchdogContent } from "./ComplianceWatchdog";
import { Activity, ShieldAlert } from "lucide-react";

const severityBadge: Record<string, "default" | "destructive" | "secondary"> = {
  critical: "destructive",
  warning: "secondary",
  info: "default",
};

const sourceColors: Record<string, string> = {
  BSEE: "bg-blue-600 text-white",
  IMCA: "bg-emerald-600 text-white",
  MTS: "bg-purple-600 text-white",
  internal: "bg-teal text-white",
};

const EmployeeSafetyBulletins = () => {
  const { user, fullName, employeePermissions } = useAuth();
  const queryClient = useQueryClient();
  const [ackDialogOpen, setAckDialogOpen] = useState(false);
  const [selectedBulletin, setSelectedBulletin] = useState<any>(null);
  const [standdownDate, setStanddownDate] = useState("");
  const [standdownNotes, setStanddownNotes] = useState("");
  const [attendeeInput, setAttendeeInput] = useState("");
  const [attendees, setAttendees] = useState<string[]>([]);
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [expandedBulletinId, setExpandedBulletinId] = useState<string | null>(null);
  const [expandedBulletinName, setExpandedBulletinName] = useState("");
  const [activeMainTab, setActiveMainTab] = useState("bulletins");

  // Track reading time for the currently expanded bulletin
  useDocumentReadingTime(expandedBulletinId, expandedBulletinName, "safety_bulletin", !!expandedBulletinId);

  const isCaptain = useMemo(() => {
    if (!employeePermissions?.employee_role) return false;
    const role = employeePermissions.employee_role.toLowerCase();
    return role === "captain" || role === "master" || role === "master/ captain" || role === "master/captain";
  }, [employeePermissions]);

  const { data: bulletins = [], isLoading } = useQuery({
    queryKey: ["safety-bulletins"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("safety_bulletins")
        .select("*")
        .eq("is_active", true)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: acknowledgments = [] } = useQuery({
    queryKey: ["safety-bulletin-acks", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("safety_bulletin_acknowledgments" as any)
        .select("*");
      if (error) throw error;
      return data as any[];
    },
    enabled: !!user?.id,
  });

  const { data: reviews = [] } = useQuery({
    queryKey: ["safety-bulletin-reviews", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("safety_bulletin_reviews" as any)
        .select("*");
      if (error) throw error;
      return data as any[];
    },
    enabled: !!user?.id,
  });

  const reviewMutation = useMutation({
    mutationFn: async (bulletinId: string) => {
      const { error } = await supabase.from("safety_bulletin_reviews" as any).insert({
        bulletin_id: bulletinId,
        user_id: user!.id,
        user_name: fullName || user!.email,
      } as any);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["safety-bulletin-reviews"] });
      toast.success("Review confirmed — thank you");
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to confirm review");
    },
  });

  const ackMutation = useMutation({
    mutationFn: async (payload: {
      bulletin_id: string;
      standdown_date: string;
      standdown_notes: string;
      attendees: string[];
    }) => {
      const { error } = await supabase.from("safety_bulletin_acknowledgments" as any).insert({
        bulletin_id: payload.bulletin_id,
        captain_user_id: user!.id,
        standdown_carried_out: true,
        standdown_date: payload.standdown_date,
        standdown_notes: payload.standdown_notes,
        attendees: payload.attendees,
      } as any);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["safety-bulletin-acks"] });
      toast.success("Safety stand-down acknowledged and recorded");
      setAckDialogOpen(false);
      resetForm();
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to submit acknowledgment");
    },
  });

  const resetForm = () => {
    setStanddownDate("");
    setStanddownNotes("");
    setAttendeeInput("");
    setAttendees([]);
    setSelectedBulletin(null);
  };

  const handleOpenAck = (bulletin: any) => {
    setSelectedBulletin(bulletin);
    setAckDialogOpen(true);
  };

  const handleAddAttendee = () => {
    const name = attendeeInput.trim();
    if (name && !attendees.includes(name)) {
      setAttendees([...attendees, name]);
      setAttendeeInput("");
    }
  };

  const handleRemoveAttendee = (name: string) => {
    setAttendees(attendees.filter((a) => a !== name));
  };

  const handleSubmitAck = () => {
    if (!selectedBulletin || !standdownDate) {
      toast.error("Please provide the stand-down date");
      return;
    }
    if (attendees.length === 0) {
      toast.error("Please add at least one attendee");
      return;
    }
    ackMutation.mutate({
      bulletin_id: selectedBulletin.id,
      standdown_date: new Date(standdownDate).toISOString(),
      standdown_notes: standdownNotes,
      attendees,
    });
  };

  const getAckForBulletin = (bulletinId: string) =>
    acknowledgments.find((a: any) => a.bulletin_id === bulletinId && a.captain_user_id === user?.id);

  const getReviewForBulletin = (bulletinId: string) =>
    reviews.find((r: any) => r.bulletin_id === bulletinId && r.user_id === user?.id);

  const getReviewCountForBulletin = (bulletinId: string) =>
    reviews.filter((r: any) => r.bulletin_id === bulletinId).length;

  const handleDownload = async (filePath: string, fileName: string) => {
    const { data } = await supabase.storage.from("operating-documents").createSignedUrl(filePath, 60);
    if (data?.signedUrl) {
      const a = document.createElement("a");
      a.href = data.signedUrl;
      a.download = fileName;
      a.target = "_blank";
      a.click();
    }
  };

  // Sort bulletins by published_date (or created_at fallback), newest first
  const sortedBulletins = useMemo(() => {
    return [...bulletins].sort((a, b) => {
      const dateA = (a as any).published_date || a.created_at;
      const dateB = (b as any).published_date || b.created_at;
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });
  }, [bulletins]);

  // Group by source
  const sources = useMemo(() => {
    const s = new Set<string>();
    sortedBulletins.forEach((b) => s.add((b as any).source || "internal"));
    return Array.from(s);
  }, [sortedBulletins]);

  const filteredBulletins = useMemo(() => {
    if (sourceFilter === "all") return sortedBulletins;
    return sortedBulletins.filter((b) => (b as any).source === sourceFilter);
  }, [sortedBulletins, sourceFilter]);

  const unreviewedCount = sortedBulletins.filter((b) => !getReviewForBulletin(b.id)).length;

  const groupedBySource = useMemo(() => {
    const groups: Record<string, typeof filteredBulletins> = {};
    filteredBulletins.forEach((b) => {
      const src = (b as any).source || "internal";
      if (!groups[src]) groups[src] = [];
      groups[src].push(b);
    });
    return groups;
  }, [filteredBulletins]);

  const sourceOrder = ["BSEE", "IMCA", "MTS", "internal"];
  const sourceLabels: Record<string, string> = {
    BSEE: "BSEE Safety Alerts",
    IMCA: "IMCA Safety Flashes",
    MTS: "MTS DP Committee Publications",
    internal: "Internal Bulletins",
  };

  const BulletinCard = ({ b }: { b: any }) => {
    const ack = getAckForBulletin(b.id);
    const review = getReviewForBulletin(b.id);
    const reviewCount = getReviewCountForBulletin(b.id);
    const pubDate = b.published_date ? new Date(b.published_date + "T00:00:00") : new Date(b.created_at);
    const isLocked = b.access_level === "member";

    return (
      <Card
        className={`hover:shadow-md transition-shadow cursor-pointer ${expandedBulletinId === b.id ? "ring-2 ring-teal/30" : ""} ${!review ? "border-l-4 border-l-amber-500" : ""}`}
        onClick={() => { setExpandedBulletinId(b.id); setExpandedBulletinName(b.title); }}
      >
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div className="flex flex-col items-center gap-1 flex-shrink-0 pt-0.5">
                <Badge className={`text-[10px] px-1.5 py-0 ${sourceColors[b.source] || sourceColors.internal}`}>
                  {b.source || "VO"}
                </Badge>
                {b.bulletin_number && (
                  <span className="text-[10px] text-muted-foreground font-mono font-bold">
                    {b.bulletin_number}
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-display text-sm text-foreground leading-tight">{b.title}</h3>
                <p className="text-xs text-muted-foreground font-body mt-1">
                  {pubDate.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                  <span className="text-muted-foreground/40 mx-1.5">·</span>
                  <span className="capitalize">{b.category}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <Badge variant={severityBadge[b.severity] || "default"} className="text-[10px]">{b.severity}</Badge>
              {isLocked && <Lock className="w-3.5 h-3.5 text-muted-foreground/40" />}
              {review && (
                <Badge variant="default" className="bg-green-600 text-white text-[10px]">
                  <CheckCircle2 className="w-3 h-3 mr-0.5" /> Reviewed
                </Badge>
              )}
            </div>
          </div>

          <p className="text-muted-foreground font-body text-xs leading-relaxed ml-16">{b.content}</p>

          {/* Actions row */}
          <div className="flex items-center gap-2 mt-3 ml-16 flex-wrap">
            <span className="text-[10px] text-muted-foreground/50 font-body flex items-center gap-1">
              <Eye className="w-3 h-3" /> {reviewCount} reviewed
            </span>

            {b.pdf_url && (
              <a href={b.pdf_url} target="_blank" rel="noopener noreferrer">
                <Button onClick={() => toast.success("Generating: PDF", { description: "Your file will download shortly." })} size="sm" variant="outline" className="h-6 px-2 text-[10px]">
                  <Download className="w-3 h-3 mr-1" /> PDF
                </Button>
              </a>
            )}

            {b.external_url && (
              <a href={b.external_url} target="_blank" rel="noopener noreferrer">
                <Button onClick={() => toast.success("Action recorded: Source")} size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-muted-foreground">
                  <ExternalLink className="w-3 h-3 mr-1" /> Source
                </Button>
              </a>
            )}

            {b.file_path && (
              <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => handleDownload(b.file_path!, b.title)}>
                <Download className="w-3 h-3 mr-1" /> Attachment
              </Button>
            )}
          </div>

          {/* Review confirmation */}
          {!review && (
            <div className="mt-3 ml-16 pt-2 border-t border-border">
              <Button
                size="sm"
                variant="outline"
                onClick={() => reviewMutation.mutate(b.id)}
                disabled={reviewMutation.isPending}
                className="border-amber-500 text-amber-600 hover:bg-amber-50 h-7 text-xs"
              >
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                {reviewMutation.isPending ? "Confirming..." : "Confirm I Have Reviewed This Bulletin"}
              </Button>
            </div>
          )}

          {/* Captain stand-down */}
          {isCaptain && !ack && (
            <div className="mt-2 ml-16">
              <Button size="sm" onClick={() => handleOpenAck(b)} className="bg-amber-600 hover:bg-amber-700 text-white h-7 text-xs">
                <Shield className="w-3.5 h-3.5 mr-1.5" />
                Acknowledge & Record Stand-Down
              </Button>
            </div>
          )}

          {/* Stand-down details */}
          {ack && (
            <div className="mt-3 ml-16 bg-muted/30 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                <span className="font-display text-xs text-foreground">Safety Stand-Down Completed</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-[10px] text-muted-foreground font-body">
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  <span>Stand-down: {new Date(ack.standdown_date).toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  <span>Acknowledged: {new Date(ack.acknowledged_at).toLocaleString()}</span>
                </div>
              </div>
              {ack.standdown_notes && <p className="text-[10px] text-muted-foreground font-body mt-1">{ack.standdown_notes}</p>}
              {(ack.attendees as string[])?.length > 0 && (
                <div className="mt-1.5">
                  <div className="flex items-center gap-1 mb-1">
                    <Users className="w-3 h-3 text-muted-foreground" />
                    <span className="text-[10px] font-display text-foreground">Attendees ({(ack.attendees as string[]).length})</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(ack.attendees as string[]).map((name: string, i: number) => (
                      <Badge key={i} variant="secondary" className="text-[9px]">{name}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };


  return (
    <IntranetLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-foreground">QHSE</h1>
          <p className="text-sm text-muted-foreground font-body mt-1">
            Bulletins · Training · HSE Suite · Compliance Watchdog
          </p>
        </div>
        {activeMainTab === "bulletins" && unreviewedCount > 0 && (
          <Badge variant="destructive" className="text-sm px-3 py-1">
            {unreviewedCount} unreviewed
          </Badge>
        )}
      </div>

      {/* Main section tabs */}
      <Tabs value={activeMainTab} onValueChange={setActiveMainTab} className="mb-6">
        <TabsList className="bg-muted/50 flex-wrap h-auto">
          <TabsTrigger value="bulletins" className="text-xs gap-1.5">
            <Shield className="w-3.5 h-3.5" /> Bulletins
          </TabsTrigger>
          <TabsTrigger value="hse-course" className="text-xs gap-1.5">
            <GraduationCap className="w-3.5 h-3.5" /> HSE Training
          </TabsTrigger>
          <TabsTrigger value="hse-suite" className="text-xs gap-1.5">
            <Activity className="w-3.5 h-3.5" /> HSE Suite
          </TabsTrigger>
          <TabsTrigger value="watchdog" className="text-xs gap-1.5">
            <ShieldAlert className="w-3.5 h-3.5" /> Compliance Watchdog
          </TabsTrigger>
        </TabsList>

        <TabsContent value="hse-course" className="mt-6">
          <VelocityHSECourse />
        </TabsContent>

        <TabsContent value="hse-suite" className="mt-6">
          <HSESuiteContent />
        </TabsContent>

        <TabsContent value="watchdog" className="mt-6">
          <ComplianceWatchdogContent />
        </TabsContent>

        <TabsContent value="bulletins" className="mt-6">

      {/* Source filter tabs */}
      <Tabs value={sourceFilter} onValueChange={setSourceFilter} className="mb-6">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="all" className="text-xs">
            All ({sortedBulletins.length})
          </TabsTrigger>
          {sourceOrder.filter((s) => sources.includes(s)).map((s) => (
            <TabsTrigger key={s} value={s} className="text-xs">
              {s === "internal" ? "Internal" : s} ({sortedBulletins.filter((b) => (b as any).source === s).length})
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" />
        </div>
      ) : filteredBulletins.length === 0 ? (
        <Card>
          <CardContent className="p-16 text-center">
            <Shield className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground font-body">No safety bulletins found</p>
          </CardContent>
        </Card>
      ) : sourceFilter === "all" ? (
        // Grouped view
        <div className="space-y-8">
          {sourceOrder
            .filter((s) => groupedBySource[s]?.length > 0)
            .map((source) => (
              <div key={source}>
                <div className="flex items-center gap-3 mb-4">
                  <Badge className={`${sourceColors[source]} text-xs`}>{source === "internal" ? "VO" : source}</Badge>
                  <h2 className="font-display text-lg text-foreground">{sourceLabels[source]}</h2>
                  <span className="text-xs text-muted-foreground font-body">
                    {groupedBySource[source].length} bulletin{groupedBySource[source].length !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className="space-y-3">
                  {groupedBySource[source].map((b) => (
                    <BulletinCard key={b.id} b={b} />
                  ))}
                </div>
              </div>
            ))}
        </div>
      ) : (
        // Flat view for filtered
        <div className="space-y-3">
          {filteredBulletins.map((b) => (
            <BulletinCard key={b.id} b={b} />
          ))}
        </div>
      )}

      {/* Stand-down Dialog */}
      <Dialog open={ackDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setAckDialogOpen(open); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Acknowledge Safety Bulletin</DialogTitle>
          </DialogHeader>
          {selectedBulletin && (
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="font-display text-sm text-foreground">{selectedBulletin.title}</p>
                <p className="text-xs text-muted-foreground font-body mt-1 line-clamp-2">{selectedBulletin.content}</p>
              </div>

              <div>
                <label className="text-sm font-display text-foreground block mb-1">Safety Stand-Down Date & Time *</label>
                <Input type="datetime-local" value={standdownDate} onChange={(e) => setStanddownDate(e.target.value)} className="font-body" />
              </div>

              <div>
                <label className="text-sm font-display text-foreground block mb-1">Notes</label>
                <Textarea value={standdownNotes} onChange={(e) => setStanddownNotes(e.target.value)} placeholder="Any additional notes about the stand-down meeting..." className="font-body" rows={2} />
              </div>

              <div>
                <label className="text-sm font-display text-foreground block mb-1">Attendees *</label>
                <div className="flex gap-2">
                  <Input value={attendeeInput} onChange={(e) => setAttendeeInput(e.target.value)} placeholder="Employee name" className="font-body" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddAttendee(); } }} />
                  <Button type="button" size="sm" variant="outline" onClick={handleAddAttendee}>Add</Button>
                </div>
                {attendees.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {attendees.map((name, i) => (
                      <Badge key={i} variant="secondary" className="text-xs cursor-pointer hover:bg-destructive/20" onClick={() => handleRemoveAttendee(name)}>
                        {name} ×
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAckDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmitAck} disabled={ackMutation.isPending} className="bg-amber-600 hover:bg-amber-700 text-white">
              {ackMutation.isPending ? "Submitting..." : "Confirm Stand-Down"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </TabsContent>
      </Tabs>
    </IntranetLayout>
  );
};

export default EmployeeSafetyBulletins;
